import { skipToken } from "@reduxjs/toolkit/query";
import { useMemo } from "react";
import Guid from "_shared/guids/guid";
import { useSession } from "users/__session/session-context";
import { useGetReplacedWorkDeparturesQuery } from "work/proposals/_api/proposal-api";
import VendorTeamDeparture from "work/work-agreements/teams/vendor-team/vendor-team-departure";

const noDepartures: readonly VendorTeamDeparture[] = [];

/**
 * Who has left the vendor team of the work a change order replaces (#6096).
 *
 * A rate keeps the name of a member who left live work, and the change order opened from that work
 * copies it. The change order's own team no longer has them, so its fee schedule would only say "Not
 * on the vendor team"; the work it replaces says how they left, so the rate can read as departed.
 *
 * Asked of the proposal, not of the work (`GET work/proposals/{id}/replaced-work-departures`): a
 * reviewer carried forward onto the change order may read it without any access to the work.
 *
 * Empty while it loads, for a proposal that replaces no work, and for a reader the vendor team is
 * restricted from: the rate then keeps the off-team flag, which is still true.
 */
export default function useReplacedWorkDepartures(
  proposalId: Guid | undefined,
  replacesWorkAgreementId: Guid | undefined,
): readonly VendorTeamDeparture[] {
  const session = useSession();
  const query = useGetReplacedWorkDeparturesQuery(
    proposalId && replacesWorkAgreementId
      ? { proposalId: proposalId.value, replacesWorkAgreementId: replacesWorkAgreementId.value, session }
      : skipToken,
  );
  return useMemo(
    () => (query.data ? VendorTeamDeparture.fromJSONArray(query.data) : noDepartures),
    [query.data],
  );
}
